import { uploadData } from "aws-amplify/storage";

import { createResizeImageFile } from "./resize-image";

import type { LangCode, S3Object } from "../gql/__generated__/graphql";

export type URLAndS3Object = {
	url: string;
	s3Object: S3Object;
};

export function hasS3Object(s3Object?: S3Object | null) {
	if (!s3Object) return false;

	return !!s3Object.key;
}

export function getURL(s3Object: S3Object): string {
	if (!s3Object.key) {
		return "";
	}

	return import.meta.env.VITE_STORAGE_AWSS3_BASE_URL + s3Object.key;
}

const AWSS3_BUCKET = import.meta.env.VITE_STORAGE_AWSS3_BUCKET;
const AWSS3_REGION = import.meta.env.VITE_STORAGE_AWSS3_REGION;

export async function getStringHTML(s3Object?: S3Object | null): Promise<string> {
	const defaultValue = Promise.resolve("<p></p>");
	if (!s3Object) {
		return defaultValue;
	}

	const url = getURL(s3Object);

	if (!url) {
		return defaultValue;
	}

	return fetch(url).then((res) => res.text().then((html) => html));
}

async function uploadHtml(path: string, html: string): Promise<S3Object> {
	const fileName = `${new Date().getTime()}.html`;
	const uniquePath = `${path}/${fileName}`;

	await uploadData({
		key: uniquePath,
		data: html,
		options: {
			accessLevel: "guest",
		},
	}).result;

	const bucket = AWSS3_BUCKET;
	const key = `public/${uniquePath}`;
	const region = AWSS3_REGION;

	return {
		bucket,
		key,
		region,
	};
}

export async function postHTML({
	html, //
	hotelID,
	resourceID,
	lang,
}: {
	html: string;
	hotelID: string;
	resourceID: string;
	lang: LangCode;
}) {
	const path = `hotel/${hotelID}/article/${resourceID}/${lang}`;
	const s3Object = await uploadHtml(path, html);

	return {
		url: getURL(s3Object),
		s3Object,
	} satisfies URLAndS3Object;
}

/**
 * AWS S3に画像をアップロードする
 * @param path
 * @param file
 * @returns
 */
export async function uploadImage(path: string, file: File): Promise<S3Object> {
	const suffix: string = file.name.split(".").pop()!;
	const fileName = `${path}/${new Date().getTime()}.${suffix}`;

	/**
	 * 画像のリサイズ
	 */
	const { resizedImageFile } = await createResizeImageFile({ file });

	await uploadData({
		key: fileName,
		data: resizedImageFile,
		options: {
			accessLevel: "guest",
		},
	}).result;

	const bucket = AWSS3_BUCKET;
	const key = `public/${fileName}`;
	const region = AWSS3_REGION;

	return {
		bucket,
		key,
		region,
	};
}

/**
 * AWS S3にJSONをアップロードする
 * @param path
 * @param file
 * @returns
 */
export async function uploadJSON<T>(path: string, object: T): Promise<S3Object> {
	await uploadData({
		key: path,
		data: JSON.stringify(object),
		options: {
			accessLevel: "guest",
		},
	}).result;

	const bucket = AWSS3_BUCKET;
	const key = `public/${path}`;
	const region = AWSS3_REGION;

	return {
		bucket,
		key,
		region,
	};
}

export async function postImage({
	file, //
	ownerID,
	resourceID,
}: {
	file: File;
	ownerID: string;
	resourceID: string;
}) {
	const path = `hotel/${ownerID}/contents/${resourceID}/${new Date().getTime()}`;
	const s3Object = await uploadImage(path, file);

	return {
		url: getURL(s3Object),
		s3Object,
	} satisfies URLAndS3Object;
}

export async function fetchJsonObject<T>(key: string): Promise<T> {
	const url = import.meta.env.VITE_STORAGE_AWSS3_BASE_URL + key;
	return fetch(url).then((res) => res.json());
}

export async function fetchMarkdown(key: string): Promise<string> {
	const url = import.meta.env.VITE_STORAGE_AWSS3_BASE_URL + key;
	return fetch(url).then((res) => res.text());
}
